import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Trash2, Loader2, AlertTriangle, ArrowUp, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Cestino() {
  const { user } = useAuth();
  const canDelete = user && (user.role === "superadmin" || user.role === "titolare");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [deleteIds, setDeleteIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const [emptyConfirmOpen, setEmptyConfirmOpen] = useState(false);
  const [emptyOtpOpen, setEmptyOtpOpen] = useState(false);
  const [emptyChallenge, setEmptyChallenge] = useState(null);
  const [emptyCode, setEmptyCode] = useState("");
  const [emptyBusy, setEmptyBusy] = useState(false);
  const [emptyProgressOpen, setEmptyProgressOpen] = useState(false);
  const [emptyJob, setEmptyJob] = useState({ total: 0, deleted: 0, status: "running" });
  const [emptyDone, setEmptyDone] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/studio/pratiche/cestino/list");
      setRows(data);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  const restore = async (ids) => {
    const list = ids || [...selected];
    if (!list.length) return;
    setBusy(true);
    try {
      await api.post("/studio/pratiche/cestino/restore", { ids: list });
      toast.success(`${list.length} ${list.length === 1 ? "pratica ripristinata" : "pratiche ripristinate"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openDelete = (ids) => {
    if (!ids.length) return;
    setDeleteIds(ids);
    setConfirmOpen(true);
  };

  const requestDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/studio/pratiche/cestino/delete/request", { ids: deleteIds });
      setChallenge(data);
      setConfirmOpen(false);
      setOtpOpen(true);
      toast.info(`Codice OTP inviato a ${data.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/studio/pratiche/cestino/delete/confirm", { challenge_id: challenge.challenge_id, code });
      setOtpOpen(false); setCode(""); setChallenge(null);
      if (!data.job_id) { toast.info("Nessuna pratica da eliminare"); load(); return; }
      setSelected(new Set());
      setEmptyJob({ total: data.total, deleted: 0, status: "running" });
      setEmptyDone(false);
      setEmptyProgressOpen(true);
      startPolling(data.job_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const requestEmpty = async () => {
    setEmptyBusy(true);
    try {
      const { data } = await api.post("/studio/pratiche/cestino/empty/request", {});
      setEmptyChallenge(data);
      setEmptyConfirmOpen(false);
      setEmptyOtpOpen(true);
      toast.info(`Codice OTP inviato a ${data.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEmptyBusy(false); }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/studio/pratiche/cestino/empty/status/${jobId}`);
        setEmptyJob({ total: data.total, deleted: data.deleted, status: data.status });
        if (data.status === "done") {
          clearInterval(pollRef.current); pollRef.current = null;
          setEmptyDone(true);
          toast.success(`${data.deleted} ${data.deleted === 1 ? "pratica eliminata" : "pratiche eliminate"} definitivamente`);
          load();
        }
      } catch (e) { /* transiente: continua il polling */ }
    }, 1000);
  };

  const confirmEmpty = async () => {
    setEmptyBusy(true);
    try {
      const { data } = await api.post("/studio/pratiche/cestino/empty/confirm", { challenge_id: emptyChallenge.challenge_id, code: emptyCode });
      setEmptyOtpOpen(false); setEmptyCode(""); setEmptyChallenge(null);
      setEmptyJob({ total: data.total, deleted: 0, status: "running" });
      setEmptyDone(false);
      setEmptyProgressOpen(true);
      startPolling(data.job_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEmptyBusy(false); }
  };

  return (
    <div data-testid="cestino-page">
      <PageHeader
        title="Cestino"
        subtitle="Pratiche spostate nel cestino. Puoi ripristinarle o (solo Superadmin) eliminarle definitivamente."
        icon={Trash2}
      />

      {canDelete && rows.length > 0 && (
        <div className="mb-4 flex justify-end">
          <Button onClick={() => setEmptyConfirmOpen(true)} disabled={busy || emptyBusy} data-testid="cestino-empty-btn"
            variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10">
            <Trash2 className="h-4 w-4 mr-1.5" /> Svuota cestino ({rows.length})
          </Button>
        </div>
      )}

      <div className="pgp-card overflow-x-auto pgp-fade-up">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm" data-testid="cestino-empty">Il cestino è vuoto.</div>
        ) : (
          <table className="w-full text-sm" data-testid="cestino-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                <th className="px-5 py-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="cestino-select-all" />
                </th>
                <th className="px-5 py-3">N. Pratica</th>
                <th className="px-5 py-3">Assistito</th>
                <th className="px-5 py-3">Stato precedente</th>
                <th className="px-5 py-3">Spostata il</th>
                <th className="px-5 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((p) => (
                <tr key={p.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`cestino-row-${p.id}`}>
                  <td className="px-5 py-3.5">
                    <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`cestino-check-${p.id}`} />
                  </td>
                  <td className="px-5 py-3.5 font-mono text-amber-400">{p.numero_pratica}</td>
                  <td className="px-5 py-3.5 font-medium text-slate-100">{p.nome_assistito || p.assistito_ragione_sociale || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-400">{p.stato_prima_cestino || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-400 font-mono text-xs">{formatDate(p.cestino_at)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" onClick={() => restore([p.id])} disabled={busy} data-testid={`cestino-row-restore-${p.id}`}
                        className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                        <ArrowUp className="h-4 w-4 mr-1" /> Ripristina
                      </Button>
                      {canDelete && (
                        <Button size="sm" onClick={() => openDelete([p.id])} disabled={busy} data-testid={`cestino-row-delete-${p.id}`}
                          className="h-8 bg-red-600 hover:bg-red-700 text-white font-semibold">
                          <Trash2 className="h-4 w-4 mr-1" /> Elimina definitivamente
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0d1220] border-t border-slate-700 px-6 py-4 flex items-center justify-between shadow-2xl" data-testid="cestino-action-bar">
          <span className="text-slate-200 font-medium" data-testid="cestino-counter">{selected.size} pratiche selezionate</span>
          <div className="flex gap-3">
            <Button onClick={() => restore()} disabled={busy} data-testid="cestino-restore-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              <ArrowUp className="h-4 w-4 mr-1.5" /> Ripristina selezionate
            </Button>
            {canDelete && (
              <Button onClick={() => openDelete([...selected])} disabled={busy} data-testid="cestino-delete-btn" className="bg-red-600 hover:bg-red-700 text-white font-semibold">
                <Trash2 className="h-4 w-4 mr-1.5" /> Elimina definitivamente selezionate
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Conferma cancellazione definitiva */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-md" data-testid="cestino-confirm-dialog">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2 text-red-400"><AlertTriangle className="h-5 w-5" /> Cancellazione definitiva</DialogTitle></DialogHeader>
          <div className="rounded-md border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200" data-testid="cestino-warning">
            Attenzione: questa operazione è irreversibile. Le pratiche selezionate verranno eliminate permanentemente.
          </div>
          <p className="text-sm text-slate-400">Dopo la conferma verrà inviato un codice OTP alla tua email per autorizzare l'eliminazione di <b>{deleteIds.length}</b> {deleteIds.length === 1 ? "pratica" : "pratiche"}.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} className="text-slate-300">Annulla</Button>
            <Button onClick={requestDelete} disabled={busy} data-testid="cestino-confirm-otp-request" className="bg-red-600 hover:bg-red-700 text-white font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma e invia OTP"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inserimento OTP (cancellazione selettiva) */}
      <Dialog open={otpOpen} onOpenChange={setOtpOpen}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-sm" data-testid="cestino-otp-dialog">
          <DialogHeader><DialogTitle className="font-head">Inserisci codice OTP</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">Abbiamo inviato un codice a <b>{challenge?.email}</b>. Inseriscilo per completare la cancellazione definitiva.</p>
          <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} placeholder="______" data-testid="cestino-otp-input"
            className="text-center text-2xl font-mono tracking-[0.5em] bg-slate-900/60 border-slate-700" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOtpOpen(false)} className="text-slate-300">Annulla</Button>
            <Button onClick={confirmDelete} disabled={busy || code.length < 6} data-testid="cestino-otp-confirm" className="bg-red-600 hover:bg-red-700 text-white font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Svuota cestino: conferma */}
      <Dialog open={emptyConfirmOpen} onOpenChange={setEmptyConfirmOpen}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-md" data-testid="cestino-empty-confirm-dialog">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2 text-red-400"><AlertTriangle className="h-5 w-5" /> Svuota cestino</DialogTitle></DialogHeader>
          <div className="rounded-md border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200" data-testid="cestino-empty-warning">
            Attenzione: questa operazione è irreversibile. Verranno eliminate definitivamente <b>{rows.length}</b> {rows.length === 1 ? "pratica" : "pratiche"} presenti nel cestino.
          </div>
          <p className="text-sm text-slate-400">Dopo la conferma verrà inviato <b>un solo</b> codice OTP alla tua email per autorizzare l'intera operazione.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmptyConfirmOpen(false)} className="text-slate-300">Annulla</Button>
            <Button onClick={requestEmpty} disabled={emptyBusy} data-testid="cestino-empty-otp-request" className="bg-red-600 hover:bg-red-700 text-white font-semibold">
              {emptyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma e invia OTP"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Svuota cestino: OTP */}
      <Dialog open={emptyOtpOpen} onOpenChange={setEmptyOtpOpen}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-sm" data-testid="cestino-empty-otp-dialog">
          <DialogHeader><DialogTitle className="font-head">Inserisci codice OTP</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">Abbiamo inviato un codice a <b>{emptyChallenge?.email}</b>. Inseriscilo per svuotare definitivamente il cestino ({emptyChallenge?.count} {emptyChallenge?.count === 1 ? "pratica" : "pratiche"}).</p>
          <Input value={emptyCode} onChange={(e) => setEmptyCode(e.target.value)} maxLength={6} placeholder="______" data-testid="cestino-empty-otp-input"
            className="text-center text-2xl font-mono tracking-[0.5em] bg-slate-900/60 border-slate-700" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmptyOtpOpen(false)} className="text-slate-300">Annulla</Button>
            <Button onClick={confirmEmpty} disabled={emptyBusy || emptyCode.length < 6} data-testid="cestino-empty-otp-confirm" className="bg-red-600 hover:bg-red-700 text-white font-semibold">
              {emptyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Svuota cestino"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Svuota cestino: avanzamento + report */}
      <Dialog open={emptyProgressOpen} onOpenChange={(o) => { if (!o && emptyDone) setEmptyProgressOpen(false); }}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-md" data-testid="cestino-empty-progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              {emptyDone ? <><CheckCircle2 className="h-5 w-5 text-emerald-400" /> Eliminazione completata</> : <><Loader2 className="h-5 w-5 animate-spin text-amber-400" /> Eliminazione in corso…</>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Progress value={emptyJob.total ? Math.round((emptyJob.deleted / emptyJob.total) * 100) : 100} data-testid="cestino-empty-progress-bar" />
            <p className="text-sm text-slate-300 text-center font-mono" data-testid="cestino-empty-progress-text">
              {emptyJob.deleted} / {emptyJob.total} eliminate
            </p>
            {emptyDone && (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-200 text-center" data-testid="cestino-empty-report">
                Operazione completata: <b>{emptyJob.deleted}</b> {emptyJob.deleted === 1 ? "pratica eliminata" : "pratiche eliminate"} definitivamente.
              </div>
            )}
          </div>
          {emptyDone && (
            <DialogFooter>
              <Button onClick={() => setEmptyProgressOpen(false)} data-testid="cestino-empty-close" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">Chiudi</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
